/** Card the BIN belongs to, as api-checkouts resolved it. */
export interface InstallmentsCard {
  brand: string;
  type: string;
  isInternational: boolean;
}

/** One installment option. `code` (the number of installments) plus
 * `financing` identify the plan: send that same pair back in the charge as
 * `installments: { code, financing }`. Amounts are minor-unit integer strings
 * (e.g. `"10442"` is 104.42 ARS), never floats, exactly as the API sends them. */
export interface InstallmentPlan {
  code: number;
  financing: string;
  installmentAmount: string;
  totalAmount: string;
  interestAmount: string;
  interestFree: boolean;
}

/** Payload of the PAN handle's `installments` event. */
export interface InstallmentsResult {
  card: InstallmentsCard;
  currency: string;
  plans: InstallmentPlan[];
}

/** Payload of the PAN handle's `installmentsError` event. `code` is the API's
 * own `code` when its error body carries one (e.g. `"1019"` BIN not found);
 * otherwise an SDK code: `network_error`, `malformed_response`,
 * `rate_limited` (429) or `http_error`. `status` is the HTTP status, absent
 * only for `network_error`. */
export interface InstallmentsError {
  code: string;
  status?: number;
}

export type InstallmentsOutcome =
  | { status: 'ok'; result: InstallmentsResult }
  | { status: 'error'; error: InstallmentsError }
  | { status: 'aborted' };

export type ApiBaseUrl = { url: string } | { disabled: 'empty' | 'invalid' };

/** Validates the build-time `VITE_API_BASE_URL` constant with the same
 * two-tier empty/invalid check as `VITE_SECURE_FIELDS_ORIGIN`, but does NOT
 * throw: only installments depend on it, so a bad value disables them
 * instead of breaking `new Menta()` for every merchant. */
export function resolveApiBaseUrl(value: unknown): ApiBaseUrl {
  if (typeof value !== 'string' || value.length === 0) return { disabled: 'empty' };
  try {
    new URL(value);
  } catch {
    return { disabled: 'invalid' };
  }
  return { url: value.replace(/\/+$/, '') };
}

/** What `InstallmentsFeed` needs; `InstallmentsClient` is the real one. */
export interface InstallmentsSource {
  readonly enabled: boolean;
  request(token: string, bin: string, signal: AbortSignal): Promise<InstallmentsOutcome>;
  warnDisabledOnce(): void;
}

const MINOR_UNITS = /^\d+$/;

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function isAmount(value: unknown): value is string {
  return typeof value === 'string' && MINOR_UNITS.test(value);
}

function parsePlan(value: unknown): InstallmentPlan | undefined {
  if (!isObject(value)) return undefined;
  const { code, financing, installment_amount, total_amount, interest_amount, interest_free } = value;
  if (
    typeof code !== 'number' ||
    !Number.isInteger(code) ||
    code < 1 ||
    !isNonEmptyString(financing) ||
    !isAmount(installment_amount) ||
    !isAmount(total_amount) ||
    !isAmount(interest_amount) ||
    typeof interest_free !== 'boolean'
  ) {
    return undefined;
  }
  return {
    code,
    financing,
    installmentAmount: installment_amount,
    totalAmount: total_amount,
    interestAmount: interest_amount,
    interestFree: interest_free,
  };
}

/** All-or-nothing: one malformed field anywhere rejects the whole body. */
function parseResult(body: unknown): InstallmentsResult | undefined {
  if (!isObject(body) || !isObject(body.card) || !Array.isArray(body.plans)) return undefined;
  const { brand, type, is_international } = body.card;
  if (!isNonEmptyString(brand) || !isNonEmptyString(type) || typeof is_international !== 'boolean') return undefined;
  if (!isNonEmptyString(body.currency)) return undefined;
  const plans: InstallmentPlan[] = [];
  for (const item of body.plans) {
    const plan = parsePlan(item);
    if (!plan) return undefined;
    plans.push(plan);
  }
  return { card: { brand, type, isInternational: is_international }, currency: body.currency, plans };
}

function errorCode(status: number, body: unknown): string {
  if (isObject(body) && isNonEmptyString(body.code)) return body.code;
  return status === 429 ? 'rate_limited' : 'http_error';
}

/**
 * Client for `GET {apiBaseUrl}/checkouts/{token}/installments`. Deliberately
 * a CORS simple request (GET, no custom headers, no credentials) so the
 * browser sends no preflight; api-checkouts authorizes it from the token,
 * the site key and the browser-stamped `Origin`. Never throws and never
 * retries. Only the 8-digit BIN leaves the SDK, never the PAN.
 */
export class InstallmentsClient implements InstallmentsSource {
  private warnedDisabled = false;

  constructor(
    private readonly apiBaseUrl: ApiBaseUrl,
    private readonly siteKey: string,
  ) {}

  get enabled(): boolean {
    return 'url' in this.apiBaseUrl;
  }

  /** One `console.warn` per client (i.e. per `Menta` instance). */
  warnDisabledOnce(): void {
    if (this.warnedDisabled || 'url' in this.apiBaseUrl) return;
    this.warnedDisabled = true;
    console.warn(
      `[menta] installments are disabled: VITE_API_BASE_URL is ${this.apiBaseUrl.disabled} in this menta.js build. ` +
        'The "installments" event will never fire.',
    );
  }

  async request(token: string, bin: string, signal: AbortSignal): Promise<InstallmentsOutcome> {
    if (!('url' in this.apiBaseUrl)) return { status: 'aborted' };
    const url =
      `${this.apiBaseUrl.url}/checkouts/${encodeURIComponent(token)}/installments` +
      `?bin=${encodeURIComponent(bin)}&site_key=${encodeURIComponent(this.siteKey)}`;
    let response: Response;
    try {
      response = await fetch(url, { method: 'GET', credentials: 'omit', mode: 'cors', cache: 'no-store', signal });
    } catch {
      return signal.aborted ? { status: 'aborted' } : { status: 'error', error: { code: 'network_error' } };
    }
    const body: unknown = await response.json().catch(() => undefined);
    // An abort while reading the body must stay silent, not become malformed_response.
    if (signal.aborted) return { status: 'aborted' };
    if (!response.ok) return { status: 'error', error: { code: errorCode(response.status, body), status: response.status } };
    const result = parseResult(body);
    return result
      ? { status: 'ok', result }
      : { status: 'error', error: { code: 'malformed_response', status: response.status } };
  }
}
